/**
 * @packageDocumentation
 * Translates numeric server identifiers into card symbol specifications, wiring animation assets for civilians, superheroes, and wildcards.
 */
// loadCards.ts
import * as PIXI from "pixi.js";
import type { SymbolSpec } from "./CardScreen";
import { RoundTuple } from "./server";

// === Config you may tweak to match your loader aliases ===
const BACK_ALIAS = "backcard"; // closed-side texture (replace with yours)
const civilian_wild_card = "civilian_wild_card"; // wildcard texture (replace with yours)
const hero_wild_card = "hero_wild_card"; // wildcard texture (replace with yours)

const COLORS = ["Teal", "White", "Pink", "Orange", "Rainbow"] as const;
type Color = typeof COLORS[number];

type Tier = "civilian" | "villain";

const FRAME_BY_BORDER: Record<1|2|3|4, string | undefined> = {
  1: "bronze_frame_anim",                   // No border
  2: "Silver_frame_anim",         // plays when revealed
  3: "Gold_frame_anim",
  4: "Hologram_frame_anim",
};

// default scales/offsets to preserve your current look
const DEFAULT_SCALES = {
  civilian: 1,
  villain: 1,
  frame:    1.00,
};
const DEFAULT_OFFSETS = {
  spineX: undefined as number | undefined, // keep CardScreen default if undefined
  spineY: undefined as number | undefined, // keep CardScreen default if undefined
  frameY: 0,
};

// Build asset keys from color+tier.
// Example: teal_civilian_anim_json / teal_civilian_anim_atlas; animation name "animation".
function characterKeys(color: Color, tier: Tier) {
  // Map old color/tier combinations to new asset names
  const assetMap: Record<string, { json: string, atlas: string }> = {
    "Teal_civilian": { json: "teal_civilian_anim_json", atlas: "teal_civilian_anim_atlas" },
    "Teal_villain": { json: "teal_villain_anim_json", atlas: "teal_villain_anim_atlas" },
    "White_civilian": { json: "white_civilian_anim_json", atlas: "white_civilian_anim_atlas" },
    "White_villain": { json: "white_villain_anim_json", atlas: "white_villain_anim_atlas" },
    "Pink_civilian": { json: "pink_civilian_json", atlas: "pink_civilian_atlas" },
    "Pink_villain": { json: "pink_villain_anim_json", atlas: "pink_villain_anim_atlas" },
    "Orange_civilian": { json: "orange_civilian_anim_json", atlas: "orange_civilian_anim_atlas" },
    "Orange_villain": { json: "orange_villain_anim_json", atlas: "orange_villain_anim_atlas" },
    "Rainbow_civilian": { json: "rainbow_civilian_anim_json", atlas: "rainbow_civilian_anim_atlas" },
    "Rainbow_villain": { json: "rainbow_villain_anim_json", atlas: "rainbow_villain_anim_atlas" },
  };

  const key = `${color}_${tier}`;
  const mapped = assetMap[key];
  
  if (!mapped) {
    console.error(`No asset mapping found for ${key}`);
    // Fallback to old naming
    return {
      json: `${key}_json`,
      atlas: `${key}_atlas`,
      anim: "idle_anim",
    };
  }

  return {
    json: mapped.json,
    atlas: mapped.atlas,
    anim: "idle_anim",
  };
}

function frameKeys(frameAlias: string) {
  return {
    json: `${frameAlias}_json`,
    atlas: `${frameAlias}_atlas`,
    anim:  "idle_anim",
  };
}

// Decode 1..42 into (tier, color, border)
function decode(id: number): { kind: "wildcard1" } | { kind: "wildcard2" } | {
  kind: "normal",
  tier: Tier,
  color: Color,
  border: 1 | 2 | 3 | 4
} {
  if (id === 41) return { kind: "wildcard1" };
  if (id === 42) return { kind: "wildcard2" };

  const idx0 = id - 1;                 // 0..39
  const group = Math.floor(idx0 / 5);  // 0..7 => 4 regular + 4 superhero blocks
  const cIdx  = idx0 % 5;              // color index 0..4

  const tier: Tier = group < 4 ? "civilian" : "villain";
  const border = (group % 4) + 1 as 1 | 2 | 3 | 4;
  const color = COLORS[cIdx];

  return { kind: "normal", tier, color, border };
}

export function specFromId(id: number): SymbolSpec {
  const backTexture = PIXI.Texture.from(BACK_ALIAS);
  const decoded = decode(id);

  if (decoded.kind === "wildcard1") {
    // For now: revealed side is the same back image (static). Replace if you add a wildcard spine.
    return {
      backTexture,
      frontSpriteTexture: PIXI.Texture.from(civilian_wild_card),
      frontScale: 1.0,
      frontY: 0,
    };
  }else
  if (decoded.kind === "wildcard2") {
    // For now: revealed side is the same back image (static). Replace if you add a wildcard spine.
    return {
      backTexture,
      frontSpriteTexture: PIXI.Texture.from(hero_wild_card),
      frontScale: 1.0,
      frontY: 0,
    };
  }

  // Character spine
  const { tier, color, border } = decoded;
  const char = characterKeys(color, tier);
  const frameAlias = FRAME_BY_BORDER[border];

  // Calculate spine scale with compensation for villain anim atlas scale differences
  let spineScale = DEFAULT_SCALES[tier];
  let spineX = DEFAULT_OFFSETS.spineX;
  let spineY = DEFAULT_OFFSETS.spineY;
  
  // villain_anim and other replacement files have scale:0.5 in atlas while originals have scale:0.75
  // Also have different texture dimensions affecting positioning
  // Apply compensation factors for better size matching and position adjustments
  if (char.atlas === 'pink_villain_anim_atlas') {
    // Pink villain animation which needs scale and position compensation
    spineScale *= 1;
    spineX = -23; // Adjust X position for horizontal centering
    spineY = 250; // Adjust Y position to center the character better
  } else if (char.atlas === 'white_villain_anim_atlas') {
    // White villain animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other villain animations
    spineX = 0; // Adjust X position for horizontal centering
    spineY = 240; // Adjust Y position to center the character better
  } else if (char.atlas === 'rainbow_civilian_anim_atlas') {
    // Rainbow civilian animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 0; // Adjust X position for horizontal centering
    spineY = 235; // Adjust Y position to center the character better
  } else if (char.atlas === 'teal_civilian_anim_atlas') {
    // Teal civilian animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 1; // Adjust X position for horizontal centering
    spineY = 223; // Adjust Y position to center the character better
  } else if (char.atlas === 'teal_villain_anim_atlas') {
    // Teal villain animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 0; // Adjust X position for horizontal centering
    spineY = 235; // Adjust Y position to center the character better
  } else if (char.atlas === 'orange_civilian_anim_atlas') {
    // Orange civilian animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 0; // Adjust X position for horizontal centering
    spineY = 225; // Adjust Y position to center the character better
  } else if (char.atlas === 'orange_villain_anim_atlas') {
    // Orange villain animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 0; // Adjust X position for horizontal centering
    spineY = 235; // Adjust Y position to center the character better
  } else if (char.atlas === 'rainbow_villain_anim_atlas') {
    // Rainbow villain animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 0; // Adjust X position for horizontal centering
    spineY = 240; // Adjust Y position to center the character better
  } else if (char.atlas === 'pink_civilian_atlas') {
    // Pink civilian animation which needs scale and position compensation
    spineScale *= 1; // Same compensation factor as other replacement animations
    spineX = 1; // Adjust X position for horizontal centering
    spineY = 222; // Adjust Y position to center the character better
  }

  const spec: SymbolSpec = {
    backTexture,
    spineJsonKey: char.json,
    spineAtlasKey: char.atlas,
    anim: char.anim,
    spineScale: spineScale,
    spineX: spineX,
    spineY: spineY,
  };

  // Optional frame overlay
  if (frameAlias) {
    const fr = frameKeys(frameAlias);
    spec.frameJsonKey = fr.json;
    spec.frameAtlasKey = fr.atlas;
    spec.frameAnim = fr.anim;
    spec.frameScale = DEFAULT_SCALES.frame;
    spec.frameY = DEFAULT_OFFSETS.frameY;
  }

  return spec;
}

/** Build 3 specs from the server tuple [id1,id2,id3,bonus] */
export function specsFromRoundTuple(tuple: RoundTuple): SymbolSpec[] {
  const [a,b,c] = tuple;
  return [specFromId(a), specFromId(b), specFromId(c)];
}
